import React, { useState, useCallback } from 'react';
import OriginTracer from '@atlassiansox/origin-tracing';
import Button, { LinkButton } from '@atlaskit/button/new';
import { xcss, Box } from '@atlaskit/primitives';
import { Modal } from '@atlassian/growth-pattern-library-modal';
import { StorageLimits } from '@atlassian/growth-pattern-library-storage-limits';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fireButtonAnalytics } from '../../../common/utils/analytics.tsx';
import { EXPERIMENT_NAME_AB } from '../../../common/utils/constants.tsx';
import { useExperimentEligibility } from '../../../services/check-experiment-eligibility/index.tsx';
import { useTrialDetails } from '../../../services/check-experiment-eligibility/useTrialDetails.tsx';
import starsIcon from './assets/storage_stars.svg';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const StorageModal = ({ userCanPay, upgradeUrl, storageUsedInGB }: Props) => (
	<JSErrorBoundary
		id="storageModal"
		packageName="jiraLossAversion"
		teamName="luna-growth"
		fallback="unmount"
	>
		<StorageModalContent
			userCanPay={userCanPay}
			upgradeUrl={upgradeUrl}
			storageUsedInGB={storageUsedInGB}
		/>
	</JSErrorBoundary>
);

export const StorageModalContent = ({ userCanPay, upgradeUrl, storageUsedInGB }: Props) => {
	const { formatMessage, formatDate } = useIntl();

	// since we're loading this modal, we must be eligible for the experiment
	// and therefore do not have payment on file
	const { isUrgent } = useExperimentEligibility(EXPERIMENT_NAME_AB, false);
	const { trialEndTime } = useTrialDetails();

	const origin = new OriginTracer({ product: 'jira' });
	const addPaymentDetailsUrl = origin.addToUrl(upgradeUrl);

	const [showModal, setShowModal] = useState(true);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const trialEndDate =
		trialEndTime && formatDate(new Date(trialEndTime), { day: 'numeric', month: 'long' });

	const onClose = useCallback(() => {
		fireButtonAnalytics(createAnalyticsEvent, 'jlaCloseButton', {
			userCanPay,
			upgradeUrl,
			storageUsedInGB,
			trialEndTime,
			modal: 'jlaStorage',
		});
		setShowModal(false);
	}, [createAnalyticsEvent, storageUsedInGB, trialEndTime, upgradeUrl, userCanPay]);

	const onAddPaymentDetails = useCallback(() => {
		fireButtonAnalytics(createAnalyticsEvent, 'jlaAddPaymentButton', {
			userCanPay,
			upgradeUrl,
			storageUsedInGB,
			trialEndTime,
			modal: 'jlaStorage',
		});

		setShowModal(false);
	}, [createAnalyticsEvent, storageUsedInGB, trialEndTime, upgradeUrl, userCanPay]);

	if (!showModal || !trialEndDate) {
		return <></>;
	}

	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="jiraLossAversionStorage">
			<Modal
				width={488}
				title={formatMessage(isUrgent ? messages.urgentHeading : messages.nonUrgentHeading)}
				subtitle={formatMessage(
					userCanPay ? messages.adminSubheading : messages.nonAdminSubheading,
					{
						formattedDate: trialEndDate,
					},
				)}
				onClose={onClose}
				testId="loss-aversion.ui.loss-aversion-modal.storage"
				actions={
					<>
						<Button onClick={onClose} appearance="subtle">
							{formatMessage(userCanPay ? messages.maybeLater : messages.close)}
						</Button>
						{userCanPay && addPaymentDetailsUrl && (
							<LinkButton
								onClick={onAddPaymentDetails}
								href={addPaymentDetailsUrl}
								appearance="primary"
								target="_blank"
							>
								{formatMessage(messages.addPayment)}
							</LinkButton>
						)}
					</>
				}
			>
				<Box xcss={backgroundStyles}>
					<Box xcss={boxStyles}>
						<StorageLimits
							title={formatMessage(messages.usedStorageHeading, {
								storageUsed: storageUsedInGB.toFixed(0),
							})}
							description={formatMessage(messages.usedStorageSubheading)}
							ariaLabel={`${storageUsedInGB.toFixed(0)} GB of 250 GB (over free limit of 2 GB)`}
							used={Math.max(15, storageUsedInGB)} // we want the progress bar to be at least 15% full to show usage
							limit={250}
							baseLimit={{
								title: 'Free limit',
								limit: 2,
							}}
						/>
					</Box>
					<img
						src={starsIcon}
						// eslint-disable-next-line jira/react/no-style-attribute
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							marginTop: '36px',
						}}
						alt=""
						height={46}
						width={67}
					/>
				</Box>
			</Modal>
			<FireScreenAnalytics attributes={{ experiment: EXPERIMENT_NAME_AB }} />
		</ContextualAnalyticsData>
	);
};

const backgroundStyles = xcss({
	width: '388px',
	display: 'flex',
	backgroundColor: 'color.background.accent.blue.subtlest',
	borderRadius: '4.2px',
});

const boxStyles = xcss({
	width: '310px',
	paddingTop: 'space.300',
	paddingLeft: 'space.300',
	paddingRight: 'space.100',
	paddingBottom: 'space.600',
});
